import React, { useState, useEffect } from 'react';
import './XR.css';
import Navbar from '../components/Navbar';

const XR = () => {
  const [showMore, setShowMore] = useState({
    awareness: false,
    walkthrough: false,
    training: false,
    collaboration: false,
    monitoring: false,
  });

  const toggleShowMore = (key) => {
    setShowMore((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <div className="xr-container">
        <p className="page-path">Services / For Industries</p>
        <p className="xr-intro">
          We help industries harness the power of XR (Extended Reality) to enhance productivity, safety, and
          innovation. From immersive walkthroughs to hands-on training simulators, our solutions are
          designed to solve real-world problems with scalable, measurable outcomes.
        </p>
        

        {/* XR Awareness Workshops */}
        <div className="xr-section">
          <div className="xr-content">
            <h1 className="xr-title">XR Awareness Workshops</h1>
            <p className="subtitle1">Equip your team with XR fundamentals and future-ready insights</p>
            <p className="description">
              Introduce your team to the transformative potential of Spatial Computing through engaging,
              hands-on workshops. These sessions enable forward-thinking approaches to training,
              collaboration, and innovation across industries and academia.
            </p>
            <div className="xr-highlights">
              <h6>Highlights:</h6>
              <ul>
                <li>Industry-focused workshops introducing the fundamentals of XR</li>
                <li>Hands-on sessions with VR/AR hardware, applications, and emerging trends</li>
                <li>Customised for educators, enterprises, and innovation teams</li>
              </ul>
            </div>
          </div>
          <div className="xr-image-wrapper">
            <img src="../images/XR_workshop_l.webp" alt="XR Workshop" className="xr-image" />
          </div>
        </div>
        <hr className="xr-divider" />

        {/* Immersive Walk-throughs */}
        <div className="xr-section reverse">
          <div className="xr-content">
            <h1 className="xr-title">Immersive Walk-throughs & Simulations</h1>
            <p className="subtitle1">Bring environments to life with interactive spatial experiences</p>
            <p className="description">
              Enhance spatial awareness and decision-making through 360° or VR-based simulations for plant tours, industrial processes, and orientation programs.
            </p>
            <div className="xr-highlights">
              <h6>Highlights:</h6>
              <ul>
                <li>Hybrid models combining real-world video, 3D scans, and interactive hotspots</li>
                <li>Ideal for onboarding, marketing, safety walkthroughs, and operational training</li>
              </ul>
            </div>
          </div>
          <div className="xr-image-wrapper">
            <img src="../images/XR_walkthrough_l.webp" alt="Immersive Walkthrough" className="xr-image" />
          </div>
        </div>
        <hr className="xr-divider" />

        {/* Immersive Skill Training */}
        <div className="xr-section">
          <div className="xr-content">
            <h1 className="xr-title">Immersive Skill Training</h1>
            <p className="subtitle1">Build critical skills with safe, hands-on virtual practice</p>
            <p className="description">
              Deliver VR-based modules replicating high-risk or precision-based tasks like welding or crane operations. These immersive modules enhance muscle memory and operational efficiency in a risk-free virtual environment.
            </p>
            <div className="xr-highlights">
              <h6>Highlights:</h6>
              <ul>
                <li>Interactive VR modules replicating real-world workflows and tools</li>
                <li>Designed for manufacturing, healthcare, engineering and other sectors</li>
                <li>Built-in performance tracking and feedback mechanisms</li>
              </ul>
            </div>
          </div>
          <div className="xr-image-wrapper">
            <img src="../images/XR_training_l.webp" alt="Immersive Skill Training" className="xr-image" />
          </div>
        </div>
        <hr className="xr-divider" />

        {/* Remote XR Collaboration */}
        <div className="xr-section reverse">
          <div className="xr-content">
            <h1 className="xr-title">Remote XR Collaboration</h1>
            <p className="subtitle1">Enhance teamwork with immersive, real-time virtual environments</p>
            <p className="description">
              Facilitate spatially connected collaboration through virtual rooms that support natural interaction, enabling seamless remote guidance, reviews, and planning sessions.
            </p>
            <div className="xr-highlights">
              <h6>Highlights:</h6>
              <ul>
                <li>Virtual spaces with avatars, voice/video chat, and shared tools</li>
                <li>Real-time collaboration on documents and 3D models</li>
                <li>Use cases: design reviews, virtual classrooms, remote project planning</li>
              </ul>
            </div>
          </div>
          <div className="xr-image-wrapper">
            <img src="../images/XR_remote_l.webp" alt="Remote Collaboration" className="xr-image" />
          </div>
        </div>
        <hr className="xr-divider" />

        {/* Spatial Monitoring & Management */}
        <div className="xr-section">
          <div className="xr-content">
            <h1 className="xr-title">Spatial Monitoring & Management Systems</h1>
            <p className="subtitle1">Smart, immersive control systems for dynamic decision-making</p>
            <p className="description">
              Deploy spatially intelligent dashboards and control rooms that deliver real-time situational awareness and data-driven insights. These immersive solutions enhance monitoring and decision-making in critical environments.
            </p>
            {showMore.monitoring && (
              <p className="extra-text">
                These systems integrate 3D spatial data with live sensor inputs for efficient facility monitoring and decision-making.
              </p>
            )}
            <div className="xr-highlights">
              <h6>Highlights:</h6>
              <ul>
                <li>Real-time monitoring with 3D environments and live data overlays</li>
                <li>Context-aware alerts and actionable analytics</li>
                <li>Applications in factory operations, smart campuses, and infrastructure management</li>
              </ul>
            </div>
          </div>
          <div className="xr-image-wrapper">
            <img src="../images/XR_monitor_l.webp" alt="Monitoring Systems" className="xr-image" />
          </div>
        </div>
        <hr className="xr-divider" />
      </div>
    </>
  );
};

export default XR;
